/**
 * Localized KennisBank data loader — performance-optimised with dynamic imports.
 * See blogLocalized.js for the design rationale.
 */
import { KENNISBANK as KB_NL } from "@/data/kennisbank";

const cache = { nl: KB_NL };
const pending = {};

const loaders = {
  en: () => import(/* webpackChunkName: "kb-en" */ "@/data/kennisbank.en.js"),
  de: () => import(/* webpackChunkName: "kb-de" */ "@/data/kennisbank.de.js"),
  fr: () => import(/* webpackChunkName: "kb-fr" */ "@/data/kennisbank.fr.js"),
  es: () => import(/* webpackChunkName: "kb-es" */ "@/data/kennisbank.es.js"),
  it: () => import(/* webpackChunkName: "kb-it" */ "@/data/kennisbank.it.js"),
  pt: () => import(/* webpackChunkName: "kb-pt" */ "@/data/kennisbank.pt.js"),
  pl: () => import(/* webpackChunkName: "kb-pl" */ "@/data/kennisbank.pl.js"),
  sv: () => import(/* webpackChunkName: "kb-sv" */ "@/data/kennisbank.sv.js"),
  da: () => import(/* webpackChunkName: "kb-da" */ "@/data/kennisbank.da.js"),
  fi: () => import(/* webpackChunkName: "kb-fi" */ "@/data/kennisbank.fi.js"),
  no: () => import(/* webpackChunkName: "kb-no" */ "@/data/kennisbank.no.js"),
  cs: () => import(/* webpackChunkName: "kb-cs" */ "@/data/kennisbank.cs.js"),
  sk: () => import(/* webpackChunkName: "kb-sk" */ "@/data/kennisbank.sk.js"),
  hu: () => import(/* webpackChunkName: "kb-hu" */ "@/data/kennisbank.hu.js"),
  ro: () => import(/* webpackChunkName: "kb-ro" */ "@/data/kennisbank.ro.js"),
  bg: () => import(/* webpackChunkName: "kb-bg" */ "@/data/kennisbank.bg.js"),
  el: () => import(/* webpackChunkName: "kb-el" */ "@/data/kennisbank.el.js"),
  et: () => import(/* webpackChunkName: "kb-et" */ "@/data/kennisbank.et.js"),
  lv: () => import(/* webpackChunkName: "kb-lv" */ "@/data/kennisbank.lv.js"),
  lt: () => import(/* webpackChunkName: "kb-lt" */ "@/data/kennisbank.lt.js"),
  hr: () => import(/* webpackChunkName: "kb-hr" */ "@/data/kennisbank.hr.js"),
  sl: () => import(/* webpackChunkName: "kb-sl" */ "@/data/kennisbank.sl.js"),
  ga: () => import(/* webpackChunkName: "kb-ga" */ "@/data/kennisbank.ga.js"),
  mt: () => import(/* webpackChunkName: "kb-mt" */ "@/data/kennisbank.mt.js"),
  zh: () => import(/* webpackChunkName: "kb-zh" */ "@/data/kennisbank.zh.js"),
  ar: () => import(/* webpackChunkName: "kb-ar" */ "@/data/kennisbank.ar.js"),
};

/** Async: load + cache the KennisBank dictionary for a locale. */
export async function getKennisbankAsync(localeCode) {
  if (!localeCode || localeCode === "nl") return KB_NL;
  if (cache[localeCode]) return cache[localeCode];
  if (pending[localeCode]) return pending[localeCode];
  const loader = loaders[localeCode];
  if (!loader) return KB_NL;
  pending[localeCode] = loader()
    .then((mod) => {
      const data = mod.KENNISBANK || mod.default || KB_NL;
      cache[localeCode] = data;
      delete pending[localeCode];
      return data;
    })
    .catch(() => {
      cache[localeCode] = KB_NL;
      delete pending[localeCode];
      return KB_NL;
    });
  return pending[localeCode];
}

export function getKennisbank(localeCode) {
  return cache[localeCode] || KB_NL;
}

export function isKennisbankFallback(slug, localeCode) {
  if (!localeCode || localeCode === "nl") return false;
  const dict = cache[localeCode];
  if (!dict || dict === KB_NL) return true;
  return !dict[slug];
}
